import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const THEME_STORAGE_KEY = 'edvise-theme';
export const THEME_OPTIONS = ['light', 'dark', 'system'];

/**
 * @param {'light' | 'dark' | 'system'} preference
 * @returns {'light' | 'dark'}
 */
export function resolveTheme(preference) {
  if (preference === 'light' || preference === 'dark') {
    return preference;
  }
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Apply resolved theme to <html> (class + data attribute for CSS / meta).
 * @param {'light' | 'dark'} resolved
 */
export function applyResolvedTheme(resolved) {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? '#09090b' : '#f9fafb');
  }
}

function readStoredPreference() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (THEME_OPTIONS.includes(stored)) {
      return stored;
    }
  } catch {
    // ignore private-mode / blocked storage
  }
  return 'system';
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [resolved, setResolved] = useState(() => resolveTheme(readStoredPreference()));

  const applyPreference = useCallback((nextPreference) => {
    const nextResolved = resolveTheme(nextPreference);
    applyResolvedTheme(nextResolved);
    setResolved(nextResolved);
  }, []);

  useEffect(() => {
    applyPreference(preference);
  }, [preference, applyPreference]);

  useEffect(() => {
    if (preference !== 'system') {
      return undefined;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyPreference('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference, applyPreference]);

  const setPreference = useCallback((next) => {
    if (!THEME_OPTIONS.includes(next)) {
      return;
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setPreferenceState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    const order = THEME_OPTIONS;
    const index = order.indexOf(preference);
    setPreference(order[(index + 1) % order.length]);
  }, [preference, setPreference]);

  const value = useMemo(
    () => ({
      preference,
      resolved,
      isDark: resolved === 'dark',
      setPreference,
      toggleTheme,
    }),
    [preference, resolved, setPreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
